/**
 * Classification of Config entries for the admin config panel: which ones are feature flags
 * (toggle switches), which area they belong to, and which editor the others get.
 * Pure functions, no Vue, so they are unit-testable.
 */

export interface ConfigEntry {
  key: string;
  value: string;
  type?: "string" | "boolean";
  description?: string;
  defaultValue?: string;
  updatedBy?: string;
  updatedAt?: string | Date;
}

export type ConfigGroup = "reports" | "tickets" | "classifier" | "ai" | "login" | "performance" | "other";

/** Display order of the groups. */
export const CONFIG_GROUPS: ConfigGroup[] = ["reports", "tickets", "classifier", "ai", "login", "performance", "other"];

/** Editor used for a non-flag setting. */
export type ConfigEditor = "number" | "text" | "longtext" | "json";

// First match wins, so the narrow rules come first.
const GROUP_RULES: Array<[RegExp, ConfigGroup]> = [
  [/classifier|category_suggestions|category_aliases/i, "classifier"],
  [/^ai[._]|^ai$|_prompt$|gemini|^faq$|postmortem/i, "ai"],
  [/auth|login|oauth/i, "login"],
  [/cache|ttl|projection|patch_events|stats/i, "performance"],
  [/simple_ticket|ticket|unreportable|staff_id_redaction/i, "tickets"],
  [/report|jury|overwatch|conciliation|penalty|duplicate|idle_autoclose|staff_review|user_score|deadline/i, "reports"],
];

export function groupOf(key: string): ConfigGroup {
  for (const [rx, group] of GROUP_RULES) if (rx.test(key)) return group;
  return "other";
}

function parseObject(value: string | undefined): Record<string, unknown> | null {
  const v = (value ?? "").trim();
  if (!v.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(v);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function isBooleanText(value: string | undefined): boolean {
  const v = (value ?? "").trim().toLowerCase();
  return v === "true" || v === "false";
}

/** A plain boolean entry (type boolean, or a true/false value). */
export function isPlainFlag(c: ConfigEntry): boolean {
  return c.type === "boolean" || isBooleanText(c.value);
}

/** A JSON object with a boolean `enabled` field (DUPLICATE_GUARD, REPORT_AUTO_ASSIGN...). */
export function isJsonFlag(c: ConfigEntry): boolean {
  if (isPlainFlag(c)) return false;
  const obj = parseObject(c.value);
  return !!obj && typeof obj.enabled === "boolean";
}

export function isFlag(c: ConfigEntry): boolean {
  return isPlainFlag(c) || isJsonFlag(c);
}

/** On/off state of a flag entry. */
export function flagState(c: ConfigEntry): boolean {
  if (isPlainFlag(c)) return (c.value ?? "").trim().toLowerCase() === "true";
  return parseObject(c.value)?.enabled === true;
}

/** On/off default, or null when the code default is not known (or not a flag value). */
export function flagDefault(c: ConfigEntry): boolean | null {
  if (c.defaultValue === undefined || c.defaultValue === null) return null;
  const probe: ConfigEntry = { ...c, value: c.defaultValue };
  return isFlag(probe) ? flagState(probe) : null;
}

/** Value to store after flipping a flag. JSON flags keep their other fields. */
export function withFlagState(c: ConfigEntry, next: boolean): string {
  if (isPlainFlag(c)) return next ? "true" : "false";
  return JSON.stringify({ ...(parseObject(c.value) ?? {}), enabled: next });
}

function normalizeJson(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value));
  } catch {
    return value;
  }
}

/** True when the default is known and the current value differs from it (JSON compared structurally). */
export function isChanged(c: ConfigEntry): boolean {
  if (c.defaultValue === undefined || c.defaultValue === null) return false;
  const a = (c.value ?? "").trim();
  const b = c.defaultValue.trim();
  if (isBooleanText(a) && isBooleanText(b)) return a.toLowerCase() !== b.toLowerCase();
  return normalizeJson(a) !== normalizeJson(b);
}

export function editorOf(c: ConfigEntry): ConfigEditor {
  const v = c.value ?? "";
  if (parseObject(v) || /^\s*\[/.test(v)) return "json";
  if (/^\s*-?\d+(\.\d+)?\s*$/.test(v)) return "number";
  if (v.includes("\n") || v.length > 80) return "longtext";
  return "text";
}

/** Validates the draft for an editor; returns an i18n key suffix of the error, or "" when valid. */
export function validateDraft(editor: ConfigEditor, draft: string): "" | "invalidJson" | "invalidNumber" {
  if (editor === "json") {
    try {
      JSON.parse(draft);
    } catch {
      return "invalidJson";
    }
  }
  if (editor === "number" && !/^\s*-?\d+(\.\d+)?\s*$/.test(draft)) return "invalidNumber";
  return "";
}

export function prettyJson(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

/** Safe i18n path segment for a key (dots and dashes become underscores). */
export function i18nKeyOf(key: string): string {
  return key.replace(/[^A-Za-z0-9]+/g, "_");
}
